import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { isAncestorOfActiveElement, $ } from 'vscode/vscode/vs/base/browser/dom';
import { Sash } from 'vscode/vscode/vs/base/browser/ui/sash/sash';
import { disposableTimeout } from 'vscode/vscode/vs/base/common/async';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { Emitter, Event } from 'vscode/vscode/vs/base/common/event';
import { Disposable, DisposableStore, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ServiceCollection } from 'vscode/vscode/vs/platform/instantiation/common/serviceCollection';
import { ILayoutService } from 'vscode/vscode/vs/platform/layout/browser/layoutService.service';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import { editorBackground } from 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import { inputBackground } from 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import { quickInputForeground, quickInputBackground } from 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { showChatView } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat';
import { ChatWidget } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chatWidget';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService.service';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';

var QuickChat_1;
let QuickChatService = class QuickChatService extends Disposable {
    constructor(quickInputService, chatService, instantiationService) {
        super();
        this.quickInputService = quickInputService;
        this.chatService = chatService;
        this.instantiationService = instantiationService;
        this._onDidClose = this._register(( new Emitter()));
        this.onDidClose = this._onDidClose.event;
    }
    get enabled() {
        return !!this.chatService.isEnabled(ChatAgentLocation.Panel);
    }
    get focused() {
        const widget = this._input?.widget;
        if (!widget) {
            return false;
        }
        return isAncestorOfActiveElement(widget);
    }
    toggle(options) {
        if (this.focused && !options?.query) {
            this.close();
        }
        else {
            this.open(options);
            if (options?.isPartialQuery) {
                const disposable = this._store.add(Event.once(this.onDidClose)(() => {
                    this._currentChat?.clearValue();
                    this._store.delete(disposable);
                }));
            }
        }
    }
    open(options) {
        if (this._input) {
            if (this._currentChat && options?.query) {
                this._currentChat.focus();
                this._currentChat.setValue(options.query, options.selection);
                if (!options.isPartialQuery) {
                    this._currentChat.acceptInput();
                }
                return;
            }
            return this.focus();
        }
        const disposableStore = ( new DisposableStore());
        this._input = this.quickInputService.createQuickWidget();
        this._input.contextKey = 'chatInputVisible';
        this._input.ignoreFocusOut = true;
        disposableStore.add(this._input);
        this._container ??= $('.interactive-session');
        this._input.widget = this._container;
        this._input.show();
        if (!this._currentChat) {
            this._currentChat = this.instantiationService.createInstance(QuickChat);
            this._currentChat.render(this._container);
        }
        else {
            this._currentChat.show();
        }
        disposableStore.add(this._input.onDidHide(() => {
            disposableStore.dispose();
            this._currentChat.hide();
            this._input = undefined;
            this._onDidClose.fire();
        }));
        this._currentChat.focus();
        if (options?.query) {
            this._currentChat.setValue(options.query, options.selection);
            if (!options.isPartialQuery) {
                this._currentChat.acceptInput();
            }
        }
    }
    focus() {
        this._currentChat?.focus();
    }
    close() {
        this._input?.dispose();
        this._input = undefined;
    }
    async openInChatView() {
        await this._currentChat?.openChatView();
        this.close();
    }
};
QuickChatService = ( __decorate([
    ( __param(0, IQuickInputService)),
    ( __param(1, IChatService)),
    ( __param(2, IInstantiationService))
], QuickChatService));
let QuickChat = class QuickChat extends Disposable {
    static { QuickChat_1 = this; }
    static { this.DEFAULT_MIN_HEIGHT = 200; }
    static { this.DEFAULT_HEIGHT_OFFSET = 100; }
    constructor(instantiationService, contextKeyService, chatService, layoutService, viewsService) {
        super();
        this.instantiationService = instantiationService;
        this.contextKeyService = contextKeyService;
        this.chatService = chatService;
        this.layoutService = layoutService;
        this.viewsService = viewsService;
        this.maintainScrollTimer = this._register(( new MutableDisposable()));
        this._deferUpdatingDynamicLayout = false;
    }
    clear() {
        this.model?.dispose();
        this.model = undefined;
        this.updateModel();
        this.widget.inputEditor.setValue('');
    }
    focus(selection) {
        if (this.widget) {
            this.widget.focusInput();
            const value = this.widget.inputEditor.getValue();
            if (value) {
                this.widget.inputEditor.setSelection(selection ?? {
                    startLineNumber: 1,
                    startColumn: 1,
                    endLineNumber: 1,
                    endColumn: value.length + 1
                });
            }
        }
    }
    hide() {
        this.widget.setVisible(false);
        this.maintainScrollTimer.value = disposableTimeout(() => {
            this.maintainScrollTimer.clear();
        }, 30 * 1000);
    }
    show() {
        this.widget.setVisible(true);
        if (this._deferUpdatingDynamicLayout) {
            this._deferUpdatingDynamicLayout = false;
            this.widget.updateDynamicChatTreeItemLayout(2, this.maxHeight);
        }
        if (!this.maintainScrollTimer.value) {
            this.widget.layoutDynamicChatTreeItemMode();
        }
    }
    render(parent) {
        if (this.widget) {
            throw ( new Error('Cannot render quick chat twice'));
        }
        const scopedInstantiationService = this.instantiationService.createChild(( new ServiceCollection([
            IContextKeyService,
            this._register(this.contextKeyService.createScoped(parent))
        ])));
        this.widget = this._register(scopedInstantiationService.createInstance(ChatWidget, ChatAgentLocation.Panel, { resource: true }, { renderInputOnTop: true, renderStyle: 'compact', menus: { inputSideToolbar: MenuId.ChatInputSide } }, {
            listForeground: quickInputForeground,
            listBackground: quickInputBackground,
            inputEditorBackground: inputBackground,
            resultEditorBackground: editorBackground
        }));
        this.widget.render(parent);
        this.widget.setVisible(true);
        this.widget.setDynamicChatTreeItemLayout(2, this.maxHeight);
        this.updateModel();
        this.sash = this._register(( new Sash(
            parent,
            { getHorizontalSashTop: () => parent.offsetHeight },
            { orientation: 1  }
        )));
        this.registerListeners(parent);
    }
    get maxHeight() {
        return this.layoutService.mainContainerDimension.height - QuickChat_1.DEFAULT_HEIGHT_OFFSET;
    }
    registerListeners(parent) {
        this._register(this.layoutService.onDidLayoutMainContainer(() => {
            if (this.widget.visible) {
                this.widget.updateDynamicChatTreeItemLayout(2, this.maxHeight);
            }
            else {
                this._deferUpdatingDynamicLayout = true;
            }
        }));
        this._register(this.widget.inputEditor.onDidChangeModelContent((e) => {
            this._currentQuery = this.widget.inputEditor.getValue();
        }));
        this._register(this.widget.onDidClear(() => this.clear()));
        this._register(this.widget.onDidChangeHeight((e) => this.sash.layout()));
        const width = parent.offsetWidth;
        this._register(this.sash.onDidStart(() => {
            this.widget.isDynamicChatTreeItemLayoutEnabled = false;
        }));
        this._register(this.sash.onDidChange((e) => {
            if (e.currentY < QuickChat_1.DEFAULT_MIN_HEIGHT || e.currentY > this.maxHeight) {
                return;
            }
            this.widget.layout(e.currentY, width);
            this.sash.layout();
        }));
        this._register(this.sash.onDidReset(() => {
            this.widget.isDynamicChatTreeItemLayoutEnabled = true;
            this.widget.layoutDynamicChatTreeItemMode();
        }));
    }
    async acceptInput() {
        return this.widget.acceptInput();
    }
    async openChatView() {
        const widget = await showChatView(this.viewsService);
        if (!widget?.viewModel || !this.model) {
            return;
        }
        for (const request of this.model.getRequests()) {
            if (request.response?.response.value || request.response?.result) {
                const message = [];
                for (const item of request.response.response.value) {
                    if (item.kind === 'textEditGroup') {
                        for (const group of item.edits) {
                            message.push({
                                kind: 'textEdit',
                                edits: group,
                                uri: item.uri
                            });
                        }
                    }
                    else {
                        message.push(item);
                    }
                }
                this.chatService.addCompleteRequest(widget.viewModel.sessionId, request.message, request.variableData, request.attempt, {
                    message,
                    result: request.response.result,
                    followups: request.response.followups
                });
            }
        }
        const value = this.widget.inputEditor.getValue();
        if (value) {
            widget.inputEditor.setValue(value);
        }
        widget.focusInput();
    }
    setValue(value, selection) {
        this.widget.inputEditor.setValue(value);
        this.focus(selection);
    }
    clearValue() {
        this.widget.inputEditor.setValue('');
    }
    updateModel() {
        this.model ??= this.chatService.startSession(ChatAgentLocation.Panel, CancellationToken.None);
        if (!this.model) {
            throw ( new Error('Could not start chat session'));
        }
        this.widget.setModel(this.model, { inputValue: this._currentQuery });
    }
};
QuickChat = QuickChat_1 = ( __decorate([
    ( __param(0, IInstantiationService)),
    ( __param(1, IContextKeyService)),
    ( __param(2, IChatService)),
    ( __param(3, ILayoutService)),
    ( __param(4, IViewsService))
], QuickChat));

export { QuickChatService };
